import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { UseFormSetError, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { integrationsApi } from "../api/integrations.api";
import type { Integration, ModuleConfiguration } from "../types";
import { integrationsKeys } from "./useIntegrations";

import { handleProblemDetailsError, ProblemDetails } from "@/lib/helpers/handleProblemDetailsError";
import useToast from "@/lib/hooks/useToast";
import type { ConfigurationContext } from "@/types/configuration";

// Query key factory for integration configuration
export const integrationConfigurationKeys = {
  all: ['integrationConfiguration'] as const,
  schemas: () => [...integrationConfigurationKeys.all, 'schema'] as const,
  schema: (integrationName: string, context: ConfigurationContext) => 
    [...integrationConfigurationKeys.schemas(), integrationName, context] as const,
  values: () => [...integrationConfigurationKeys.all, 'values'] as const,
  value: (integrationName: string, context: ConfigurationContext) => 
    [...integrationConfigurationKeys.values(), integrationName, context] as const,
};

// Hook to fetch integration configuration schema
export function useIntegrationConfigurationSchema(
  integrationName: string,
  context: ConfigurationContext,
  enabled = true
) {
  return useQuery({
    queryKey: integrationConfigurationKeys.schema(integrationName, context),
    queryFn: () => integrationsApi.fetchIntegrationConfigurationSchema(integrationName, context),
    staleTime: 20 * 60 * 1000, // 20 minutes
    enabled,
  });
}

// Hook to fetch current integration configuration
export function useIntegrationConfiguration(
  integrationName: string,
  context: ConfigurationContext,
  enabled = true
) {
  return useQuery({
    queryKey: integrationConfigurationKeys.value(integrationName, context),
    queryFn: () => integrationsApi.fetchIntegrationConfiguration(integrationName, context),
    staleTime: 20 * 60 * 1000, // 20 minutes
    enabled,
  });
}

// Hook to update integration configuration
export function useUpdateIntegrationConfiguration<TFormValues extends FieldValues = FieldValues>(
  context: ConfigurationContext,
  options?: {
    getFormMethods?: () => {
      setError?: UseFormSetError<TFormValues>;
      getValues?: () => TFormValues;
    };
  }
) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      integrationName,
      configurations,
      isActive,
      currentIsActive: _currentIsActive,
    }: {
      integrationName: string;
      configurations: ModuleConfiguration;
      isActive: boolean;
      currentIsActive: boolean;
    }) => {
      await integrationsApi.updateIntegrationConfigurationAtomic(
        integrationName,
        context,
        configurations,
        isActive
      );
    },
    onError: (err: unknown) => {
      const axiosError = err as AxiosError<ProblemDetails>;
      
      // Get current form methods if available
      const formMethods = options?.getFormMethods?.();
      const setError = formMethods?.setError;
      const formValues = formMethods?.getValues?.();
      
      // Use handleProblemDetailsError helper if setError is provided
      if (setError) {
        handleProblemDetailsError({
          error: axiosError,
          t,
          setError,
          formValues,
          showToast,
          fallbackMessage: 'Failed to update integration configuration',
        });
      } else {
        // Fallback: show toast if no form methods available
        const errorData = axiosError.response?.data;
        const errorDetail = errorData?.detail 
          || (errorData && typeof errorData === 'object' && 'error' in errorData ? String(errorData.error) : null)
          || (errorData && typeof errorData === 'object' && 'message' in errorData ? String(errorData.message) : null)
          || (typeof axiosError?.response?.data === 'string' ? axiosError.response.data : null)
          || axiosError?.message 
          || 'Failed to update integration configuration';
        
        showToast(errorDetail, 'error');
      }
    },
    onSuccess: (_data, variables) => {
      const { integrationName, configurations, isActive } = variables;
      
      // Update integration list in cache
      const queryParams = context.level === 'client' 
        ? { tenantId: context.tenantId, clientId: context.clientId }
        : { tenantId: context.tenantId, clientId: null };
      
      queryClient.setQueriesData(
        { queryKey: integrationsKeys.list(queryParams) },
        (old: unknown) => {
          if (!old || !Array.isArray(old)) return old;
          
          return old.map((integration: unknown) => {
            const int = integration as Integration;
            if (int.name === integrationName) {
              return { ...int, isActive, activatedDate: isActive ? new Date().toISOString() : undefined };
            }
            return int;
          });
        }
      );

      // Update configuration values in cache
      queryClient.setQueryData(
        integrationConfigurationKeys.value(integrationName, context),
        configurations
      );
      
      setTimeout(() => {
        showToast('Integration configuration updated successfully', 'success');
      }, 150);
    },
    onSettled: async (_data, _error, _variables) => {
      await queryClient.invalidateQueries({ 
        queryKey: ['integrations'],
        refetchType: 'all'
      });
    },
  });
}
